import { useEffect, useRef, useState } from 'react';

/**
 * Numero che "sale" animato verso il valore finale (count-up), stile Stripe.
 *
 * - rAF + easeOutCubic, durata ~650ms.
 * - Anima sia al primo mount (da 0) sia ai cambi di valore (dal precedente).
 * - `format` permette di formattare ogni frame (es. fmtEur, toFixed).
 * - prefers-reduced-motion → salta dritto al valore finale (no animazione).
 * - Il font è già tabular-nums lato CSS → zero jitter di larghezza.
 */
interface AnimatedNumberProps {
  value: number;
  /** Formatta il numero corrente in stringa da mostrare. Default: intero it-IT. */
  format?: (n: number) => string;
  /** Durata in ms. Default 650. */
  duration?: number;
}

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/* Stessa correzione di `fmtEur` in lib/utils.ts: senza raggruppamento forzato
   l'italiano non mette il punto ai numeri di quattro cifre, e due fasce
   affiancate scrivono le migliaia in due modi diversi. `true` a runtime
   diventa 'always' (la stringa non è ancora nei tipi di TypeScript). */
const defaultFormat = (n: number) =>
  new Intl.NumberFormat('it-IT', { maximumFractionDigits: 0, useGrouping: true }).format(Math.round(n));

export function AnimatedNumber({ value, format = defaultFormat, duration = 650 }: AnimatedNumberProps) {
  const [display, setDisplay] = useState(value);
  const fromRef = useRef(value);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    const to = value;
    const from = fromRef.current;

    // Nessun movimento richiesto, o reduced motion → set immediato
    if (from === to || prefersReducedMotion()) {
      setDisplay(to);
      fromRef.current = to;
      return;
    }

    const start = performance.now();
    const tick = (now: number) => {
      const elapsed = now - start;
      const t = Math.min(1, elapsed / duration);
      const eased = easeOutCubic(t);
      setDisplay(from + (to - from) * eased);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        fromRef.current = to;
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [value, duration]);

  return <>{format(display)}</>;
}
